import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLazyQuery } from '@apollo/client';
import { FieldError, InputField } from '../../../../../components/Form';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { measuresSelector } from '../../../../../redux/selectors/builder';
import {
  removeMeasureFormula,
  setMeasureFormulaExpression,
} from '../../../../../redux/slices/oldBuilder';
import {
  IValidateMeasureFormulaQuery,
  IValidateMeasureFormulaQueryVariables,
} from '../../../../../utils/apollo/graphqlSchema';
import { VALIDATE_MEASURE_FORMULA } from '../../../../../utils/apollo/measures';
import { getCharacterByIncrementedIndex } from '../../../../../utils/character';
import { defaultMeasureFormula } from '../../../../../redux/slices/oldBuilder/defaults';
import { SquareRemoveButton } from '../../../../../components/Button';

interface IProps {
  expression: string | null;
  validationError: string | null;
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 320px 24px;
  column-gap: 4px;
  align-items: start;
`;

const Actions = styled.div`
  padding-top: 2px;
`;

const UppercaseLetterInput = styled(InputField)`
  text-transform: uppercase;
  &::placeholder {
    text-transform: none;
  }
`;

const QueryFormulaField = ({ expression, validationError }: IProps) => {
  const [touched, setTouched] = useState(false);

  const dispatch = useAppDispatch();

  const measures = useAppSelector(measuresSelector);

  const [validateFormula] = useLazyQuery<
    IValidateMeasureFormulaQuery,
    IValidateMeasureFormulaQueryVariables
  >(VALIDATE_MEASURE_FORMULA);

  // Each measure on UI has a visually assigned letter(A,B, etc.)
  // We need array of these letters in order to validate expression input
  const measureVariables = useMemo(
    () =>
      measures.map((_, index) => getCharacterByIncrementedIndex('A', index)),
    [measures],
  );

  const validateFormulaInput = useCallback(
    async (formula: string, validateOnly?: boolean) => {
      const uppercasedFormula = formula.toUpperCase();
      const validationResult = await validateFormula({
        variables: {
          input: {
            formula: uppercasedFormula,
            variables: measureVariables,
          },
        },
      });

      const validationError =
        validationResult?.data?.measureFormulaValidate?.validationError;

      dispatch(
        setMeasureFormulaExpression({
          measureFormula: {
            expression: uppercasedFormula,
            validationError: validationError || null,
            isValid: !validationError,
          },
          validateOnly,
        }),
      );
    },
    [dispatch, measureVariables, validateFormula],
  );

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // if user removes value
    if (!newValue.length) {
      dispatch(
        setMeasureFormulaExpression({ measureFormula: defaultMeasureFormula }),
      );
    } else if (touched) {
      // Validate the formula input when user leaves the field
      await validateFormulaInput(newValue);
    }

    if (touched) {
      setTouched(false);
    }
  };

  const handleRemove = () => {
    dispatch(removeMeasureFormula());
  };

  const handleChange = () => {
    // When user start typing we will invalidate formula because
    // we don't know if the final result is valid or not until onBlur
    // event will be fired.
    if (!touched) {
      setTouched(true);

      dispatch(
        setMeasureFormulaExpression({
          measureFormula: {
            expression,
            isValid: false,
            validationError: null,
          },
        }),
      );
    }
  };

  const handleKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    if (event.code === 'Enter') {
      target.blur();
    }
  };

  useEffect(() => {
    // Validate the formula input whenever 'expression' or 'measureVariables' change.
    // This ensures the formula is validated with the latest values when user add/remove measures.
    if (expression) {
      validateFormulaInput(expression, true);
    }
  }, [expression, measureVariables, validateFormulaInput]);

  return (
    <Container>
      <div>
        <UppercaseLetterInput
          // Component will not unmount when user navigates between
          // query reports. Therefore, force React to re-render it based on
          // expression value
          key={`formula-${expression || ''}`}
          autoFocus={!expression}
          defaultValue={expression || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeydown}
          placeholder={'Build custom measure i.e. (A+B)/C'}
          hasError={Boolean(validationError)}
        />
        {validationError && (
          <FieldError type="custom" customError={validationError} />
        )}
      </div>
      <Actions>
        <SquareRemoveButton onClick={handleRemove} />
      </Actions>
    </Container>
  );
};

export default QueryFormulaField;
